import axios from "axios";

import Api from "../apiService";

class EdoDocumentsApi {
  /**
   * Получает список документов ЭДО с фильтрами и пагинацией.
   */
  index(companyId, params = {}) {
    return Api.getRequest(
      `companies/${companyId}/edo/documents`,
      params,
      Api.authHeaders()
    );
  }

  /**
   * Получает карточку одного документа ЭДО.
   */
  show(companyId, id) {
    return Api.getRequest(
      `companies/${companyId}/edo/documents/${id}`,
      {},
      Api.authHeaders()
    );
  }

  duplicate(companyId, id) {
    return Api.postRequest(
      `companies/${companyId}/edo/documents/${id}/duplicate`,
      {},
      Api.authHeaders()
    );
  }

  /**
   * Запускает синхронизацию документов из Saby за выбранный период.
   */
  sync(companyId, integrationId, dateFrom = null) {
    const body = {};
    if (integrationId) body.integration_id = integrationId;
    if (dateFrom) body.date_from = dateFrom;
    return Api.postRequest(
      `companies/${companyId}/edo/documents/sync`,
      body,
      Api.authHeaders()
    );
  }

  /**
   * Получает время последней успешной синхронизации документов.
   */
  lastSyncedAt(companyId) {
    return Api.getRequest(
      `companies/${companyId}/edo/documents/last_synced_at`,
      {},
      Api.authHeaders()
    );
  }

  /**
   * Создает первый титул ЭТрН через общий endpoint build_document.
   */
  buildTitle1(companyId, integrationId, documentData, sourceLinks = {}, mode = "send") {
    return Api.postRequest(
      `companies/${companyId}/edo/documents/build_document`,
      {
        integration_id: integrationId,
        document_data: documentData,
        build_type: "consignment_note_title1",
        order_id: sourceLinks.orderId,
        trip_id: sourceLinks.tripId,
        mode,
      },
      Api.authHeaders()
    );
  }

  /**
   * Создает ЭЗЗ через общий endpoint build_document.
   */
  buildTransportOrder(companyId, integrationId, documentData, sourceLinks = {}, mode = "send") {
    return Api.postRequest(
      `companies/${companyId}/edo/documents/build_document`,
      {
        integration_id: integrationId,
        document_data: documentData,
        build_type: "transport_order",
        order_id: sourceLinks.orderId,
        trip_id: sourceLinks.tripId,
        mode,
      },
      Api.authHeaders()
    );
  }

  /**
   * Создает поручение экспедитору, backend собирает XML 1110486.
   */
  buildForwarderOrder(companyId, integrationId, documentData, sourceLinks = {}, mode = "send") {
    return Api.postRequest(
      `companies/${companyId}/edo/documents/build_document`,
      {
        integration_id: integrationId,
        document_data: documentData,
        build_type: "forwarder_order",
        order_id: sourceLinks.orderId,
        trip_id: sourceLinks.tripId,
        mode,
      },
      Api.authHeaders()
    );
  }

  /**
   * Создает экспедиторскую расписку, backend собирает XML 1110489.
   */
  buildForwarderReceipt(companyId, integrationId, documentData, sourceLinks = {}, mode = "send") {
    return Api.postRequest(
      `companies/${companyId}/edo/documents/build_document`,
      {
        integration_id: integrationId,
        document_data: documentData,
        build_type: "forwarder_receipt",
        order_id: sourceLinks.orderId,
        trip_id: sourceLinks.tripId,
        mode,
      },
      Api.authHeaders()
    );
  }

  /**
   * Создает локальный черновик ЭДО-документа из загруженного XML.
   */
  importXml(companyId, data) {
    return Api.postRequest(
      `companies/${companyId}/edo/documents/import_xml`,
      data,
      Api.authHeaders()
    );
  }

  /**
   * Отправляет локальный черновик документа в Saby через выбранную интеграцию.
   */
  submitToProvider(companyId, documentId, integrationId) {
    return Api.postRequest(
      `companies/${companyId}/edo/documents/${documentId}/submit_to_provider`,
      { integration_id: integrationId },
      Api.authHeaders()
    );
  }

  /**
   * Обновляет локальную карточку документа данными из Saby.
   */
  refresh(companyId, documentId) {
    return Api.postRequest(
      `companies/${companyId}/edo/documents/${documentId}/refresh`,
      {},
      Api.authHeaders()
    );
  }

  /**
   * Проверяет XML-вложение документа на стороне backend.
   */
  validateXml(companyId, documentId) {
    return Api.postRequest(
      `companies/${companyId}/edo/documents/${documentId}/validate_xml`,
      {},
      Api.authHeaders()
    );
  }

  /**
   * Подтягивает workflow ЭЗЗ из Saby для диагностики следующего шага.
   */
  diagnoseWorkflow(companyId, documentId) {
    return Api.postRequest(
      `companies/${companyId}/edo/documents/${documentId}/diagnose_workflow`,
      {},
      Api.authHeaders()
    );
  }

  /**
   * Обновляет форму ЭЗЗ или локальный черновик ЭТрН; отправкой управляет backend.
   */
  updateForm(companyId, documentId, documentData, sourceLinks = {}) {
    return Api.patchRequest(
      `companies/${companyId}/edo/documents/${documentId}/update_form`,
      { document_data: documentData, ...sourceLinks },
      Api.authHeaders()
    );
  }

  /**
   * Обновляет локальные связи документа, не изменяя XML у провайдера.
   */
  updateSourceLinks(companyId, documentId, sourceLinks) {
    return Api.patchRequest(
      `companies/${companyId}/edo/documents/${documentId}/update_source_links`,
      {
        order_id: sourceLinks.orderId,
        trip_id: sourceLinks.tripId,
      },
      Api.authHeaders()
    );
  }

  // Сохраняет рабочие данные документа только в Gruz, без изменения в Saby.
  updateInternalDetails(companyId, documentId, data) {
    return Api.patchRequest(
      `companies/${companyId}/edo/documents/${documentId}`,
      data,
      Api.authHeaders()
    );
  }

  // Архивирует локальную карточку ЭДО-документа, не затрагивая документ в Saby.
  archive(companyId, documentId) {
    return Api.patchRequest(
      `companies/${companyId}/edo/documents/${documentId}/archive`,
      {},
      Api.authHeaders()
    );
  }

  // Возвращает локальную карточку ЭДО-документа из архива.
  restore(companyId, documentId) {
    return Api.patchRequest(
      `companies/${companyId}/edo/documents/${documentId}/restore`,
      {},
      Api.authHeaders()
    );
  }

  /**
   * Записывает титул 2 через общий endpoint update_attachment.
   * Даты приёмки ЭТрН передаются в ISO с offset точки погрузки.
   */
  updateTitle2(companyId, documentId, documentData) {
    return Api.patchRequest(
      `companies/${companyId}/edo/documents/${documentId}/update_attachment`,
      { document_data: documentData, attachment_type: "title2" },
      Api.authHeaders()
    );
  }

  /**
   * Записывает титул экспедитора 1110487 к поручению экспедитору.
   */
  updateForwarderOrderTitle2(companyId, documentId, documentData) {
    return Api.patchRequest(
      `companies/${companyId}/edo/documents/${documentId}/update_attachment`,
      { document_data: documentData, attachment_type: "forwarder_order_title2" },
      Api.authHeaders()
    );
  }

  /**
   * Обновляет титул клиента поручения экспедитору 1110486.
   */
  updateForwarderOrderTitle1(companyId, documentId, documentData) {
    return Api.patchRequest(
      `companies/${companyId}/edo/documents/${documentId}/update_attachment`,
      { document_data: documentData, attachment_type: "forwarder_order_title1" },
      Api.authHeaders()
    );
  }

  /**
   * Обновляет экспедиторскую расписку 1110489.
   */
  updateForwarderReceiptTitle1(companyId, documentId, documentData) {
    return Api.patchRequest(
      `companies/${companyId}/edo/documents/${documentId}/update_attachment`,
      { document_data: documentData, attachment_type: "forwarder_receipt_title1" },
      Api.authHeaders()
    );
  }

  /**
   * Записывает титул 3 ЭТрН, backend собирает XML 1110341.
   * Даты приёмки передаются в ISO с offset точки выгрузки, без конвертации браузером.
   */
  updateTitle3(companyId, documentId, documentData) {
    return Api.patchRequest(
      `companies/${companyId}/edo/documents/${documentId}/update_attachment`,
      { document_data: documentData, attachment_type: "title3" },
      Api.authHeaders()
    );
  }

  /**
   * Записывает титул 4 ЭТрН, backend собирает XML 1110342.
   * Даты выдачи передаются в ISO с offset точки выгрузки; backend сохраняет его в XML.
   */
  updateTitle4(companyId, documentId, documentData) {
    return Api.patchRequest(
      `companies/${companyId}/edo/documents/${documentId}/update_attachment`,
      { document_data: documentData, attachment_type: "title4" },
      Api.authHeaders()
    );
  }

  /**
   * Записывает титул 7 ЭТрН 1167009, уведомление о переадресовке.
   */
  updateRedirect(companyId, documentId, documentData) {
    return Api.patchRequest(
      `companies/${companyId}/edo/documents/${documentId}/update_attachment`,
      { document_data: documentData, attachment_type: "redirect" },
      Api.authHeaders()
    );
  }

  /**
   * Записывает дополнительный файл ЭТрН 1110344, замену водителя или ТС.
   */
  updateReplacement(companyId, documentId, documentData) {
    return Api.patchRequest(
      `companies/${companyId}/edo/documents/${documentId}/update_attachment`,
      { document_data: documentData, attachment_type: "replacement" },
      Api.authHeaders()
    );
  }

  /**
   * Получает справочник типов ТС для ЭДО-форм.
   */
  vehicleTypes(companyId) {
    return Api.getRequest(
      `companies/${companyId}/edo/vehicle_types`,
      {},
      Api.authHeaders()
    );
  }

  /**
   * Получает справочник типов владения ТС для ЭДО-форм.
   */
  vehicleOwnershipTypes(companyId) {
    return Api.getRequest(
      `companies/${companyId}/edo/vehicle_ownership_types`,
      {},
      Api.authHeaders()
    );
  }

  /**
   * Скачивает PDF ЭДО через backend, а не напрямую из storage.
   *
   * PDF preview читает файл как Blob через JS. Для S3 signed URL это может
   * ломаться из-за CORS storage-домена, хотя ссылка открывается в новой вкладке.
   * Backend endpoint сначала проверяет права на документ и отдаёт байты PDF со
   * своего origin, поэтому одинаково работает с локальным storage и S3.
   */
  downloadPdf(companyId, documentId) {
    return Api.getRequest(
      `companies/${companyId}/edo/documents/${documentId}/pdf`,
      {},
      Api.authHeaders(),
      { responseType: "blob" }
    );
  }

  /**
   * Скачивает файл документа с auth headers, нужен для PDF blob.
   */
  downloadFile(url) {
    if (/^https?:\/\//.test(url)) {
      return axios.get(url, { responseType: "blob" });
    }

    return Api.getRequest(
      url,
      {},
      Api.authHeaders(),
      { responseType: "blob" }
    );
  }
}

export default new EdoDocumentsApi();
